import React, { useMemo } from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, ReferenceArea, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import { goalText } from './steerText';
import { bigText } from './steerText';

// The goal chart (You page, 2026-10 restyle). Three months back, three months
// ahead. One gold line for you: solid where it's measured, dotted where it's
// where you're headed if nothing changes. The goal is a plain box. No axes,
// no grid; two small labels say "goal" and "now" and the row under the chart
// says when.

const DAY = 86400000;
const dayIndex = (iso, origin) => Math.round((new Date(`${iso}T00:00:00`) - origin) / DAY);

const SteerChart = ({ card, metric, height = 180 }) => {
  const { lo, hi } = { lo: card.ultimate_low, hi: card.ultimate_high };

  const built = useMemo(() => {
    const hist = (metric.history || []);
    const proj = (metric.projection || []);
    if (!hist.length) return null;
    const lastDate = hist[hist.length - 1].date;
    const origin = new Date(`${lastDate}T00:00:00`);
    const cutoff = -92; // ~3 months back
    const rows = [];
    hist.forEach((p) => {
      const t = dayIndex(p.date, origin);
      if (t >= cutoff) rows.push({ t, hist: p.value });
    });
    proj.forEach((p) => {
      const t = dayIndex(p.date, origin);
      if (t === 0) { const r = rows.find((x) => x.t === 0); if (r) r.proj = p.value; else rows.push({ t: 0, proj: p.value }); }
      else rows.push({ t, proj: p.value });
    });
    rows.sort((a, b) => a.t - b.t);
    const tMin = rows[0].t; const tMax = rows[rows.length - 1].t;
    const vals = [];
    rows.forEach((r) => { if (r.hist != null) vals.push(r.hist); if (r.proj != null) vals.push(r.proj); });
    if (lo != null) vals.push(lo); if (hi != null) vals.push(hi);
    let yMin = Math.min(...vals); let yMax = Math.max(...vals);
    const pad = (yMax - yMin || Math.abs(yMax) || 1) * 0.12;
    yMin = Math.max(yMin - pad, 0); yMax = yMax + pad;
    // A half-open goal ("under 60", "10,000 or more") still needs a box you
    // can see: give it at least a quarter of the chart's height.
    if (card.direction === 'down' && hi != null) yMin = Math.max(0, Math.min(yMin, hi - (yMax - hi) / 3));
    if (card.direction === 'up' && lo != null) yMax = Math.max(yMax, lo + (lo - yMin) / 3);
    // The goal box: a half-open goal runs to the chart edge.
    const boxLo = card.direction === 'down' ? yMin : (lo ?? yMin);
    const boxHi = card.direction === 'up' ? yMax : (hi ?? yMax);
    const nowVal = hist[hist.length - 1].value;
    const projEnd = proj.length ? proj[proj.length - 1].value : nowVal;
    const pct = (v) => ((yMax - v) / (yMax - yMin)) * 100;
    return {
      rows, tMin, tMax, yMin, yMax, boxLo, boxHi, nowVal, rising: projEnd > nowVal,
      splitPct: tMax === tMin ? 100 : ((0 - tMin) / (tMax - tMin)) * 100,
      goalTop: pct(boxHi), goalBottom: pct(boxLo), nowTop: pct(nowVal),
    };
  }, [metric.history, metric.projection, lo, hi, card.direction]);

  if (!built) return null;
  const { rows, tMin, tMax, yMin, yMax, boxLo, boxHi, nowVal, rising, splitPct, goalTop, goalBottom, nowTop } = built;
  const goalLabelTop = goalTop < 50 ? `calc(${goalTop}% + 6px)` : `calc(${goalBottom}% - 20px)`;
  // The "now" tag sits on the side the dotted line is leaving: above a falling
  // projection, below a rising one.
  const nowLabelTop = rising ? `calc(${nowTop}% + 10px)` : `calc(${nowTop}% - 22px)`;

  return (
    <div className="mt-2.5">
      <div className="relative" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <XAxis dataKey="t" type="number" domain={[tMin, tMax]} hide />
            <YAxis domain={[yMin, yMax]} hide />
            <ReferenceArea y1={boxLo} y2={boxHi} fill="var(--bg-elev)" fillOpacity={1}
              stroke="var(--line-strong)" strokeWidth={1} ifOverflow="visible" />
            <ReferenceLine x={0} stroke="var(--line-strong)" strokeWidth={1} />
            <Line type="monotone" dataKey="hist" stroke="var(--you)" strokeWidth={2.5}
              dot={false} isAnimationActive={false} connectNulls />
            <Line type="monotone" dataKey="proj" stroke="var(--you)" strokeWidth={2.5}
              strokeDasharray="3 7" strokeLinecap="round" dot={false} isAnimationActive={false} connectNulls />
            <Line type="monotone" dataKey="hist" stroke="none" isAnimationActive={false}
              dot={(p) => (p.payload.t === 0 && p.payload.hist != null
                ? <circle key="now" cx={p.cx} cy={p.cy} r={4.5} fill="var(--you)" />
                : null)} activeDot={false} legendType="none" />
          </ComposedChart>
        </ResponsiveContainer>
        <span className="pointer-events-none absolute left-2 text-sm leading-none text-fg-3" style={{ top: goalLabelTop }}>
          goal · {goalText(card)}
        </span>
        <span className="pointer-events-none absolute text-sm leading-none text-fg-2"
          style={{ top: nowLabelTop, left: `calc(${Math.min(splitPct, 80)}% + 10px)` }}>
          now · {bigText(nowVal, card.unit)}
        </span>
      </div>
      <div className="relative mt-1.5 flex justify-between text-sm tabular-nums text-fg-3">
        <span>3 months ago</span>
        <span className="absolute -translate-x-1/2" style={{ left: `${splitPct}%` }}>Today</span>
        <span>3 months ahead</span>
      </div>
      <div className="mt-2.5 flex flex-wrap gap-x-[18px] gap-y-1 text-sm text-fg-3">
        <span className="inline-flex items-center gap-2"><i className="inline-block w-[22px] border-t-[2.5px] border-you" />So far</span>
        <span className="inline-flex items-center gap-2"><i className="inline-block w-[22px] border-t-[2.5px] border-dotted border-you" />If nothing changes</span>
        <span className="inline-flex items-center gap-2"><i className="inline-block h-3 w-[22px] border border-line-strong bg-elev" />Goal</span>
      </div>
    </div>
  );
};

export default SteerChart;
